/* mx4ever collection pages: the current rider's size on product cards ("Simon: XL") and a results bar above the grid
   ("12 of these come in Simon's size (XL) · Show only Simon's size").
   Size data per product comes from /collections/<handle>?view=mx-sizes (templates/collection.mx-sizes.liquid),
   sizes from MxSizeEngine (mx-rider.js). Rendered by the Garage strip section on collection templates. */
(() => {
  if (customElements.get('mx-rider-results')) return;
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const gen = (x) => (/[sxz]$/i.test(String(x)) ? `${x}'` : `${x}s`); // Danish possessive
  const norm = (s) => {
    const n = String(s).toUpperCase().replace(/[\s-]+/g, '');
    const map = { SMALL: 'S', MEDIUM: 'M', LARGE: 'L', XLARGE: 'XL', XXLARGE: 'XXL', '2XL': 'XXL', XXXL: '3XL', XSMALL: 'XS', EXTRASMALL: 'XS', EXTRALARGE: 'XL' };
    return map[n] || n;
  };
  // small rider icon (same figure as the Garage strip's rider pill)
  const ICON =
    '<svg class="mx-card-size__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="8" r="4"></circle><path d="M4 21c1-4 4-6 8-6s7 2 8 6"></path></svg>';
  const SIZE_PARAM = /^filter\.v\.option\.(size|st.rrelse)$/i;

  class MxRiderResults extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.strings = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      this.bar = this.querySelector('[data-bar]');
      this.grid = document.getElementById('ProductGridContainer');
      if (!this.grid || !window.MxRiders || !window.MxSizeEngine) return;
      this.grid.before(this); // sits between the filters and the product grid
      this.loadedFor = null;
      this.update = this.update.bind(this);
      document.addEventListener('mx-rider:change', () => this.paint());
      // Dawn replaces the grid when filters/sorting change (and the URL with it)
      let queued = false;
      this.observer = new MutationObserver(() => {
        if (queued) return;
        queued = true;
        // a timer (not requestAnimationFrame, which pauses in background tabs) also lets Dawn update the URL first
        setTimeout(() => {
          queued = false;
          this.update();
        }, 60);
      });
      this.observer.observe(this.grid, { childList: true, subtree: true });
      this.update();
    }

    t(key, vars) {
      let s = this.strings[key] || key;
      if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(v);
      return s;
    }

    // data for the current filters (sorting and page don't change the set)
    dataUrl() {
      const p = new URLSearchParams(location.search);
      ['page', 'sort_by', 'view', 'section_id'].forEach((k) => p.delete(k));
      p.set('view', 'mx-sizes');
      return `${location.pathname}?${p.toString()}`;
    }

    async update() {
      const url = this.dataUrl();
      if (url !== this.loadedFor) {
        this.loadedFor = url;
        try {
          const txt = await fetch(url, { credentials: 'same-origin' }).then((r) => r.text());
          if (url !== this.loadedFor) return; // filters changed again meanwhile
          this.data = JSON.parse(txt);
        } catch (e) {
          this.data = null;
        }
      }
      this.paint();
    }

    // rider's result for one product (null = no chip: unsized, or the wrong range like a youth jersey for an adult)
    resultFor(handle, rider) {
      const p = this.data && this.data.products[handle];
      const table = p && this.data.tables[p.t];
      if (!table) return null;
      const byBody = !['helmet', 'boot', 'hat'].includes(table.category);
      const youthRider = rider.group === 'youth';
      if (byBody && youthRider !== (table.group === 'youth')) return null;
      if (byBody && table.group === 'women' && rider.group !== 'women') return null;
      const sizes = {};
      for (const v of p.v) {
        const hit = String(v)
          .split('/')
          .map((part) => {
            const row = table.rows.find((r) => [r.size, ...Object.values(r.aliases || {})].some((n) => norm(n) === norm(part)));
            return row ? row.size : null;
          });
        sizes[v] = hit.every(Boolean) ? hit : null;
      }
      const r = window.MxSizeEngine.recommend(rider, table, { use: p.use, sizes });
      return { r, category: table.category };
    }

    paint() {
      const rider = window.MxRiders.store.current();
      const cards = [...this.grid.querySelectorAll('.grid__item')];
      const fits = new Map(); // size as sold -> number of products
      let sized = 0;
      let category = null;
      for (const card of cards) {
        const link = card.querySelector('a[href*="/products/"]');
        const handle = link ? decodeURIComponent(link.pathname.split('/products/')[1] || '').split('/')[0] : '';
        const res = rider && handle ? this.resultFor(handle, rider) : null;
        let html = null;
        let none = false;
        if (res) {
          const { r } = res;
          if (r.fit === 'good' || r.fit === 'between') html = `${ICON}<span>${esc(rider.name)}</span> <strong>${esc(r.size)}</strong>`;
          else if (r.fit === 'not_offered') {
            html = `${ICON}<span>${esc(this.t('chip_none', { name: rider.name, name_gen: gen(rider.name) }))}</span>`;
            none = true;
          }
        }
        this.chip(card, html, none);
      }
      // the bar counts every product in the (filtered) collection, not just this page of the grid
      if (rider && this.data) {
        for (const handle of Object.keys(this.data.products)) {
          const res = this.resultFor(handle, rider);
          if (!res) continue;
          sized++;
          category = category || res.category;
          if (res.r.fit === 'good' || res.r.fit === 'between') fits.set(res.r.size, (fits.get(res.r.size) || 0) + 1);
        }
      }
      this.paintBar(rider, sized, fits, category);
    }

    chip(card, html, none) {
      // Dawn cards have two info blocks; the one inside .card__inner is hidden on standard cards, so use the last
      const infos = card.querySelectorAll('.card__information');
      const info = infos[infos.length - 1];
      if (!info) return;
      const old = info.querySelector('.mx-card-size');
      if (!html) {
        if (old) old.remove();
        return;
      }
      if (old && old.dataset.key === html && old.classList.contains('mx-card-size--none') === none) return; // unchanged
      const el = old || document.createElement('span');
      el.className = `mx-card-size${none ? ' mx-card-size--none' : ''}`;
      el.dataset.key = html;
      el.innerHTML = html;
      if (!old) {
        const after = info.querySelector('.card-information');
        if (after) info.insertBefore(el, after);
        else info.append(el);
      }
    }

    // links to the rider's other gear (saved by the homepage rider tab for this visit), or back to that tab
    moreLinks(rider) {
      const vars = { name: rider.name, name_gen: gen(rider.name) };
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem('mx:finder-links'));
      } catch (e) {}
      // links are saved per language (locale root '' or '/da'); only use this language's set
      const set = saved && saved.rider === `${rider.id}|${rider.updated}` && saved.byRoot && saved.byRoot[this.root().replace(/\/+$/, '')];
      if (set) {
        const here = location.pathname.replace(/\/+$/, '');
        const links = (set.order || Object.keys(set.items))
          .map((k) => set.items[k])
          .filter((it) => it && new URL(it.href, location.origin).pathname.replace(/\/+$/, '') !== here)
          .map((it) => `<a class="link underlined-link" href="${esc(it.href)}">${esc(it.label)} (${esc(it.count)})</a>`);
        if (links.length) return `<p class="mx-results__more">${esc(this.t('more_lead', vars))} ${links.join(' · ')}</p>`;
        return '';
      }
      return `<p class="mx-results__more"><a class="link underlined-link" href="${esc(this.root())}" data-to-rider-tab>${esc(this.t('more_link', vars))}</a></p>`;
    }

    root() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    paintBar(rider, sized, fits, category) {
      if (!rider || !sized) {
        this.hidden = true;
        this.bar.innerHTML = '';
        this._barHtml = '';
        return;
      }
      const sizes = [...fits.keys()].sort((a, b) => (isNaN(parseFloat(a)) || isNaN(parseFloat(b)) ? 0 : parseFloat(a) - parseFloat(b)));
      const label = sizes.length ? (category === 'boot' ? `EU ${sizes.join(' / ')}` : sizes.join(' / ')) : '';
      const vars = { name: rider.name, name_gen: gen(rider.name), sizes: label, n: [...fits.values()].reduce((a, b) => a + b, 0) };
      const params = new URLSearchParams(location.search);
      const active = [...params.entries()].filter(([k]) => SIZE_PARAM.test(k));
      // the store's size filter values that mean the rider's sizes (Search & Discovery groups "Small", "X Large" …)
      const wanted = [];
      const seen = new Set();
      document.querySelectorAll('input[name^="filter.v.option."]').forEach((input) => {
        if (!SIZE_PARAM.test(input.name) || seen.has(input.value)) return;
        const lab = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        const text = (lab ? lab.textContent : input.value).replace(/\(.*$/s, '').trim().split('\n')[0];
        if (sizes.some((s) => norm(s) === norm(text))) {
          seen.add(input.value);
          wanted.push([input.name, input.value]);
        }
      });
      const base = new URLSearchParams(location.search);
      [...base.keys()].filter((k) => SIZE_PARAM.test(k) || k === 'page').forEach((k) => base.delete(k));
      const allUrl = `${location.pathname}${base.toString() ? `?${base}` : ''}`;
      const only = new URLSearchParams(base);
      wanted.forEach(([k, v]) => only.append(k, v));
      const onlyUrl = `${location.pathname}?${only}`;
      const onlyActive =
        active.length && wanted.length && active.length === wanted.length && active.every(([, v]) => wanted.some(([, w]) => w === v));

      let html;
      if (!sizes.length) html = `<p>${esc(this.t('bar_none', vars))}</p>`;
      else if (onlyActive)
        html = `<p><strong>${esc(this.t('bar_on', vars))}</strong></p><a class="link underlined-link" href="${esc(allUrl)}">${esc(this.t('bar_all'))}</a>`;
      else
        html = `<p>${esc(this.t('bar_offer', vars))}</p>${
          wanted.length ? `<a class="link underlined-link" href="${esc(onlyUrl)}">${esc(this.t('bar_only', vars))}</a>` : ''
        }`;
      html += this.moreLinks(rider);
      if (this._barHtml !== html) {
        this._barHtml = html;
        this.bar.innerHTML = `<div class="mx-results__bar">${html}</div>`;
        const back = this.bar.querySelector('[data-to-rider-tab]');
        if (back)
          back.addEventListener('click', () => {
            try {
              sessionStorage.setItem('mx:finder-tab', 'rider');
            } catch (e) {}
          });
      }
      this.hidden = false;
    }
  }

  customElements.define('mx-rider-results', MxRiderResults);
})();
